<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { WidgetSelectMicroCreditType } from '~/components/widget/select-microcredit-type'
import {
  WidgetBestMicrocredits,
  WidgetMicroCreditsListMeiliSearch
} from '~/components/widget/microcredits'
import { WidgetSeoContent } from '~/components/widget/seo'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetFaqList } from '~/components/widget/faq-list'
import { microcreditsTypes } from '~/data/pages/microcredit/mocks'
import type { IMicrocreditCompaniesQueryParams } from '~/components/entity/microcredit/item'

definePageMeta({
  path: '/microcredit',
  alias: ['/microcredits/:type(.*)*']
})

const route = useRoute()
const { t } = useI18n()
const { pathWithoutLocale } = useLocalizedPath()

const pageInfo = reactive({
  title: '',
  description: ''
})

const currentPage = ref(Number(route.query.page) || 1)

watch(
  () => route.query.page,
  (newPage) => {
    const pageNum = Number(newPage)
    currentPage.value = !isNaN(pageNum) && pageNum > 0 ? pageNum : 1
  }
)

const creditsListQueryParams = reactive<IMicrocreditCompaniesQueryParams>({
  per_page: '20'
})

const typeSegments = Array.isArray(route.params.type) ? route.params.type : [route.params.type]
const typeParam = typeSegments[0]
if (typeParam) {
  const selectedType = microcreditsTypes.find((item) => item.type === typeParam)
  // Extra segments (/microcredits/mkk/junk) would render the type page under a second URL
  if (selectedType && typeSegments.length === 1) {
    creditsListQueryParams.filter = [typeParam]
    pageInfo.title = selectedType.title
    pageInfo.description = selectedType.description || ''
  } else {
    showError({ statusCode: 404 })
  }
} else {
  pageInfo.title = t('common.microcredits')
  pageInfo.description = t('page.microcredits.default_description')
}

const breadcrumbs = computed(() => {
  if (typeParam) {
    return [
      { title: t('common.microcredits'), url: '/microcredit' },
      { title: t(pageInfo.title), url: route.path }
    ]
  }
  return [{ title: t('common.microcredits'), url: '/microcredit' }]
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetSelectMicroCreditType :types="microcreditsTypes" />

            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />

            <WidgetBestMicrocredits
              :title="$t('common.best_microcredits_title')"
              :organization="'microcredit_company'"
              :text="$t('common.best_microcredits_text')"
            />
          </aside>
        </template>

        <div class="space-y-5" role="main">
          <WidgetSeoContent
            position="upper"
            :title="pageInfo.title"
            :description="pageInfo.description"
          />
          <WidgetMicroCreditsListMeiliSearch :query-params="creditsListQueryParams" />
          <WidgetSeoContent position="bottom" />
          <WidgetFaqList
            :query-params="{
              'filter[faqable_url]': pathWithoutLocale
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
